import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Device } from '../../types/device';
import { ArrowLeft, Info } from 'lucide-react';
import { Button } from '../../../../components/ui/button';
import { Dialog, DialogContentWithoutClose, DialogHeader, DialogTitle } from '../../../../components/ui/dialog';
import { Input } from '../../../../components/ui/input';
import { Label } from '../../../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../../components/ui/select';
import { Tabs, TabsList, TabsTrigger } from '../../../../components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../../../components/ui/table';
import { Checkbox } from '../../../../components/ui/checkbox';
import { Tooltip } from '../../../../components/ui/tooltip';

type AlarmListItem = {
  id: string | number;
  name?: string;
  alarm_text?: string;
  [key: string]: any;
};

type TimeUnit = 'seconds' | 'minutes' | 'hours';

type AlarmConfig = {
  severity: 'WARNING' | 'CRITICAL';
  suppressionWindowValue: string;
  suppressionWindowUnit: TimeUnit;
  escalationTimeoutValue: string;
  escalationTimeoutUnit: TimeUnit;
};

const valueToSeconds = (raw: string, unit: TimeUnit): number | null => {
  if (raw === '' || raw.trim() === '') return null;
  const n = Number(raw);
  if (Number.isNaN(n)) return null;
  switch (unit) {
    case 'hours':
      return Math.round(n * 3600);
    case 'minutes':
      return Math.round(n * 60);
    case 'seconds':
    default:
      return Math.round(n);
  }
};

const secondsToDisplayValue = (seconds: number, unit: TimeUnit): string => {
  if (unit === 'hours') {
    const v = seconds / 3600;
    return Number.isInteger(v) ? String(v) : String(Math.round(v * 10000) / 10000).replace(/\.?0+$/, '');
  }
  if (unit === 'minutes') {
    const v = seconds / 60;
    return Number.isInteger(v) ? String(v) : String(Math.round(v * 10000) / 10000).replace(/\.?0+$/, '');
  }
  return String(seconds);
};

const convertValueForUnitChange = (valueStr: string, fromUnit: TimeUnit, toUnit: TimeUnit): string => {
  if (valueStr === '' || valueStr.trim() === '') return '';
  const sec = valueToSeconds(valueStr, fromUnit);
  if (sec === null) return valueStr;
  return secondsToDisplayValue(sec, toUnit);
};

interface DeviceSettingsModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  devices: Device[];
  selectedDevice?: Device | null;
}

const getConfigFromAlarm = (alarm: AlarmListItem) => alarm?.config ?? alarm;

const formatNotifyRoleEmails = (alarm: AlarmListItem, role: 'operator' | 'supervisor'): string => {
  const c = getConfigFromAlarm(alarm);
  const list = c?.notify_roles?.[role];
  if (!Array.isArray(list)) return '—';
  const joined = list.map(String).filter(Boolean).join(', ');
  return joined || '—';
};

const DeviceSettingsModal: React.FC<DeviceSettingsModalProps> = ({
  open,
  onOpenChange,
  devices,
  selectedDevice,
}) => {
  const [selectedMachineType, setSelectedMachineType] = useState<string>('');
  const [selectedDeviceId, setSelectedDeviceId] = useState<string>('all');
  const [alarmList, setAlarmList] = useState<AlarmListItem[]>([]);
  const [alarmConfigs, setAlarmConfigs] = useState<Record<string, AlarmConfig>>({});
  const [selectedAlarmIds, setSelectedAlarmIds] = useState<Set<string>>(new Set());
  const [operatorsInput, setOperatorsInput] = useState<string>('');
  const [supervisorsInput, setSupervisorsInput] = useState<string>('');
  const [isLoadingAlarms, setIsLoadingAlarms] = useState<boolean>(false);
  const [isSavingAlarmConfig, setIsSavingAlarmConfig] = useState<boolean>(false);

  const filteredDevicesRef = useRef<Device[]>([]);

  const parseCommaSeparated = (value: string): string[] =>
    value
      .split(',')
      .map((s) => s.trim())
      .filter(Boolean);

  const devicesArray = Array.isArray(devices) ? devices : [];

  const machineTypes = useMemo(() => {
    const types = new Set<string>();

    devicesArray.forEach((device: Device) => {
      const coatingType = device.deviceData?.coating_type || device.deviceData?.coatingType;
      if (coatingType) {
        types.add(String(coatingType).toLowerCase());
      } else {
        types.add('impp');
      }
    });

    return Array.from(types).sort();
  }, [devicesArray]);

  const selectedDeviceIdFromProp = selectedDevice != null ? String(selectedDevice.id) : '';
  const selectedDeviceCoatingType =
    selectedDevice?.deviceData?.coating_type || selectedDevice?.deviceData?.coatingType;

  const selectedDeviceMachineType = useMemo(() => {
    return selectedDeviceCoatingType ? String(selectedDeviceCoatingType).toLowerCase() : 'impp';
  }, [selectedDeviceCoatingType]);

  const isDeviceContextLocked = Boolean(selectedDevice);

  const machineTypeForDeviceFilter = isDeviceContextLocked ? selectedDeviceMachineType : selectedMachineType;

  const filteredDevices = useMemo(() => {
    if (!machineTypeForDeviceFilter) return [];

    return devicesArray.filter((device: Device) => {
      const coatingType = device.deviceData?.coating_type || device.deviceData?.coatingType;
      const normalizedType = coatingType ? String(coatingType).toLowerCase() : '';
      if (machineTypeForDeviceFilter === 'impp') {
        return normalizedType === 'impp';
      }
      return normalizedType === machineTypeForDeviceFilter.toLowerCase();
    });
  }, [devicesArray, machineTypeForDeviceFilter]);

  filteredDevicesRef.current = filteredDevices;

  const deviceOptionsForSelect = useMemo(() => {
    if (!isDeviceContextLocked || !selectedDevice) return filteredDevices;
    const alreadyInList = filteredDevices.some((d) => String(d.id) === selectedDeviceIdFromProp);
    if (alreadyInList) return filteredDevices;
    return [selectedDevice, ...filteredDevices];
  }, [filteredDevices, isDeviceContextLocked, selectedDevice, selectedDeviceIdFromProp]);

  useEffect(() => {
    if (!open) {
      setSelectedMachineType('');
      setSelectedDeviceId('all');
      setSelectedAlarmIds(new Set());
      setOperatorsInput('');
      setSupervisorsInput('');
      setAlarmList([]);
      setAlarmConfigs({});
      setIsLoadingAlarms(false);
      setIsSavingAlarmConfig(false);
    }
  }, [open]);

  useEffect(() => {
    if (open && machineTypes.length > 0 && !selectedMachineType) {
      setSelectedMachineType(machineTypes[0]);
    }
  }, [open, machineTypes, selectedMachineType]);

  useEffect(() => {
    const initial: Record<string, AlarmConfig> = {};
    const config = (alarm: AlarmListItem) => alarm?.config ?? alarm;
    alarmList.forEach((alarm) => {
      const id = String(alarm.id);
      const c = config(alarm);
      const severity = alarm.severity === 'CRITICAL' || c?.severity === 'CRITICAL' ? 'CRITICAL' : 'WARNING';
      const suppression =
        c?.suppression_window_seconds != null
          ? String(c.suppression_window_seconds)
          : alarm.suppression_window_seconds != null
            ? String(alarm.suppression_window_seconds)
            : '';
      const escalation =
        c?.escalation_timeout_seconds != null
          ? String(c.escalation_timeout_seconds)
          : alarm.escalation_timeout_seconds != null
            ? String(alarm.escalation_timeout_seconds)
            : '';
      initial[id] = {
        severity,
        suppressionWindowValue: suppression,
        suppressionWindowUnit: 'seconds',
        escalationTimeoutValue: escalation,
        escalationTimeoutUnit: 'seconds',
      };
    });
    setAlarmConfigs((prev) => ({ ...prev, ...initial }));
  }, [alarmList]);

  useEffect(() => {
    if (!open) return;
    if (selectedDeviceIdFromProp) {
      setSelectedMachineType(selectedDeviceMachineType);
      setSelectedDeviceId(selectedDeviceIdFromProp);
      setSelectedAlarmIds(new Set());
    }
  }, [open, selectedDeviceIdFromProp, selectedDeviceMachineType]);

  useEffect(() => {
    const fetchAlarmList = async () => {
      if (!selectedMachineType || !open) {
        setAlarmList([]);
        setSelectedAlarmIds(new Set());
        return;
      }

      const params = new URLSearchParams();
      params.set('coating_type', selectedMachineType);
      if (selectedDeviceId && selectedDeviceId !== 'all') {
        const currentFiltered = filteredDevicesRef.current;
        const selectedDeviceForApi = currentFiltered.find((d: Device) => String(d.id) === selectedDeviceId);
        const edgeUid = selectedDeviceForApi?.deviceData?.deviceId ?? selectedDeviceForApi?.id ?? selectedDeviceId;
        params.set('edge_uid', String(edgeUid));
      }

      setIsLoadingAlarms(true);
      try {
        const apiUrl = process.env.REACT_APP_LIMITS_API_BASE_URL || 'http://localhost:9000';
        const endpoint = `${apiUrl}/alarm-config/alarms-list?${params.toString()}`;

        const response = await fetch(endpoint, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
          },
        });

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const data = await response.json();
        const alarms = Array.isArray(data)
          ? data
          : Array.isArray(data?.data?.data)
            ? data.data.data
            : Array.isArray(data?.data)
              ? data.data
              : Array.isArray(data?.alarms)
                ? data.alarms
                : Array.isArray(data?.results)
                  ? data.results
                  : [];

        setAlarmList(alarms);
      } catch (error) {
        console.error('Error fetching alarm list:', error);
        setAlarmList([]);
      } finally {
        setIsLoadingAlarms(false);
      }
    };

    fetchAlarmList();
  }, [open, selectedMachineType, selectedDeviceId]);

  useEffect(() => {
    if (selectedMachineType && !isDeviceContextLocked) {
      setSelectedDeviceId('all');
      setSelectedAlarmIds(new Set());
    }
  }, [selectedMachineType, isDeviceContextLocked]);

  const toggleAlarmSelection = (alarmId: string) => {
    setSelectedAlarmIds((prev) => {
      const next = new Set(prev);
      if (next.has(alarmId)) next.delete(alarmId);
      else next.add(alarmId);
      return next;
    });
  };

  const allAlarmIds = useMemo(
    () => alarmList.map((alarm) => String(alarm.id)),
    [alarmList]
  );
  const allAlarmsSelected =
    alarmList.length > 0 && selectedAlarmIds.size === alarmList.length;
  const toggleSelectAllAlarms = () => {
    if (allAlarmsSelected) {
      setSelectedAlarmIds(new Set());
    } else {
      setSelectedAlarmIds(new Set(allAlarmIds));
    }
  };

  const hasOperatorEmails = parseCommaSeparated(operatorsInput).length > 0;

  const handleSaveAlarmConfig = async () => {
    if (selectedAlarmIds.size === 0) return;
    if (!hasOperatorEmails) return;
    const isAllDevices = selectedDeviceId === 'all';
    const apiUrl = process.env.REACT_APP_LIMITS_API_BASE_URL || 'http://localhost:9000';
    const endpoint = `${apiUrl}/alarm-config/machine-type`;
    const notifyRoles = {
      operator: parseCommaSeparated(operatorsInput),
      supervisor: parseCommaSeparated(supervisorsInput),
    };

    const alarms = Array.from(selectedAlarmIds).map((alarmId) => {
      const config = alarmConfigs[alarmId] ?? {
        severity: 'WARNING' as const,
        suppressionWindowValue: '',
        suppressionWindowUnit: 'seconds' as const,
        escalationTimeoutValue: '',
        escalationTimeoutUnit: 'seconds' as const,
      };
      const parsedAlarmId = Number(alarmId);
      const normalizedAlarmId = Number.isNaN(parsedAlarmId) ? alarmId : parsedAlarmId;
      return {
        alarm_id: normalizedAlarmId,
        severity: config.severity,
        config: {
          suppression_window_seconds: valueToSeconds(
            config.suppressionWindowValue,
            config.suppressionWindowUnit ?? 'seconds'
          ),
          escalation_timeout_seconds: valueToSeconds(
            config.escalationTimeoutValue,
            config.escalationTimeoutUnit ?? 'seconds'
          ),
          notify_roles: notifyRoles,
        },
      };
    });

    const payload: Record<string, unknown> = {
      coating_type: selectedMachineType,
      alarms,
    };
    if (!isAllDevices) {
      const selectedSettingsDevice = filteredDevices.find((device: Device) => device.id === selectedDeviceId);
      payload.edge_uid = selectedSettingsDevice?.deviceData?.deviceId || selectedSettingsDevice?.id || selectedDeviceId;
    }

    try {
      setIsSavingAlarmConfig(true);
      const response = await fetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
      await response.json();
      onOpenChange(false);
    } catch (error) {
      console.error('Error saving alarm config:', error);
    } finally {
      setIsSavingAlarmConfig(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContentWithoutClose className="fixed inset-0 w-screen h-screen max-w-none max-h-none translate-x-0 translate-y-0 rounded-none flex flex-col overflow-hidden data-[state=open]:slide-in-from-none data-[state=closed]:slide-out-to-none">
        <DialogHeader className="flex flex-row items-center justify-between gap-4 space-y-0 pb-2 shrink-0">
          <div className="flex flex-row items-center gap-4 min-w-0">
            <Button
              type="button"
              variant="outline"
              size="icon"
              className="shrink-0 rounded-lg"
              onClick={() => onOpenChange(false)}
              aria-label="Back"
            >
              <ArrowLeft className="h-5 w-5 text-gray-500" />
            </Button>
            <DialogTitle className="min-w-0">Alarm Settings</DialogTitle>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <Button variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleSaveAlarmConfig}
              disabled={
                !selectedMachineType ||
                !selectedDeviceId ||
                selectedAlarmIds.size === 0 ||
                !hasOperatorEmails ||
                isSavingAlarmConfig
              }
            >
              {isSavingAlarmConfig ? 'Saving...' : 'Save'}
            </Button>
          </div>
        </DialogHeader>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-4 py-4 flex-1 min-h-0 overflow-y-auto">
          <div className="space-y-2 sm:col-span-2">
            <Label>Machine Type</Label>
            <Tabs
              value={selectedMachineType || machineTypes[0] || ''}
              onValueChange={(value) => {
                setSelectedMachineType(value);
                setSelectedDeviceId('all');
              }}
              className="w-full"
            >
              <TabsList className="h-12 w-full justify-start gap-6 border-b border-gray-200 bg-transparent px-0 pb-0">
                {machineTypes.map((type) => {
                  const displayName = type === 'impp' ? 'IMPP' : type.charAt(0).toUpperCase() + type.slice(1);
                  return (
                    <TabsTrigger
                      key={type}
                      value={type}
                      disabled={isDeviceContextLocked}
                      className="data-[state=active]:border-[#e04530] data-[state=active]:text-[#e04530] relative px-1 pb-3 pt-2 text-base font-medium text-gray-500 hover:text-gray-700 data-[state=active]:border-b-[0.5px] rounded-none"
                    >
                      {displayName}
                    </TabsTrigger>
                  );
                })}
              </TabsList>
            </Tabs>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-x-4 gap-y-4 sm:col-span-2">
            <div className="space-y-2 min-w-0">
              <Label htmlFor="device">Device</Label>
              <Select
                value={isDeviceContextLocked && selectedDeviceIdFromProp ? selectedDeviceIdFromProp : selectedDeviceId}
                onValueChange={setSelectedDeviceId}
                disabled={!selectedMachineType || isDeviceContextLocked}
              >
                <SelectTrigger id="device">
                  <SelectValue placeholder="Select device" />
                </SelectTrigger>
                <SelectContent className="max-h-72">
                  <SelectItem value="all">All Devices</SelectItem>
                  {deviceOptionsForSelect.length > 0 ? (
                    deviceOptionsForSelect.map((device: Device) => {
                      const deviceId = device.deviceData?.deviceId || device.id;
                      const value = String(device.id);
                      return (
                        <SelectItem key={value} value={value}>
                          {deviceId}
                        </SelectItem>
                      );
                    })
                  ) : null}
                  {deviceOptionsForSelect.length === 0 ? (
                    <SelectItem value="no-devices" disabled>
                      No devices available for selected machine type
                    </SelectItem>
                  ) : null}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2 min-w-0">
              <Label htmlFor="operators">
                Operators&apos; Emails <span className="text-destructive" aria-hidden>*</span>
              </Label>
              <Input
                id="operators"
                type="text"
                value={operatorsInput}
                onChange={(e) => setOperatorsInput(e.target.value)}
                placeholder="e.g. operator1@crce.com, operator2@crce.com"
                required
                aria-required="true"
              />
              <p className="text-xs text-muted-foreground">
                At least one operator email is required. Separate multiple addresses with commas.
              </p>
            </div>
            <div className="space-y-2 min-w-0">
              <Label htmlFor="supervisors">Supervisors&apos; Emails</Label>
              <Input
                id="supervisors"
                type="text"
                value={supervisorsInput}
                onChange={(e) => setSupervisorsInput(e.target.value)}
                placeholder="e.g. supervisor1@crce.com, supervisor2@crce.com"
              />
            </div>
          </div>

          <div className="space-y-2 sm:col-span-2 w-full min-w-0">
            <Label>Alarm List</Label>
            <div className="rounded-md border w-full overflow-auto">
              <Table className="w-full min-w-full" containerClassName="w-full overflow-auto">
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-10 px-2">
                      {alarmList.length > 0 ? (
                        <Checkbox
                          checked={allAlarmsSelected}
                          onCheckedChange={toggleSelectAllAlarms}
                          aria-label="Select all alarms"
                          className="translate-y-[2px]"
                        />
                      ) : null}
                    </TableHead>
                    <TableHead>Alarm List</TableHead>
                    <TableHead>Severity</TableHead>
                    <TableHead>
                      <span className="flex items-center gap-1.5">
                        Suppression Window
                        <Tooltip
                          placement="top"
                          content={
                            <div className="space-y-1 text-left text-xs">
                              <p>Time period during which additional notifications </p>
                              <p>for the same alarm are suppressed after the first notification is sent.</p>
                              <p className="pt-1">Choose unit (seconds, minutes, hours); saved as seconds.</p>
                            </div>
                          }
                          contentClassName="max-w-[220px]"
                        >
                          <Info className="h-4 w-4 text-muted-foreground cursor-help shrink-0" />
                        </Tooltip>
                      </span>
                    </TableHead>
                    <TableHead>
                      <span className="flex items-center gap-1.5">
                        Escalation Timeout
                        <Tooltip
                          placement="top"
                          content={
                            <div className="space-y-1 text-left text-xs">
                              <p>Time to wait before escalating a critical alarm if it has not been acknowledged.</p>
                              <p className="pt-1">Choose unit (seconds, minutes, hours); saved as seconds.</p>
                            </div>
                          }
                          contentClassName="max-w-[240px]"
                        >
                          <Info className="h-4 w-4 text-muted-foreground cursor-help shrink-0" />
                        </Tooltip>
                      </span>
                    </TableHead>
                    <TableHead>Operator Emails</TableHead>
                    <TableHead>Supervisor Emails</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {isLoadingAlarms ? (
                    <TableRow>
                      <TableCell colSpan={7} className="text-center py-8 text-muted-foreground">
                        Loading alarms...
                      </TableCell>
                    </TableRow>
                  ) : alarmList.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={7} className="text-center py-8 text-muted-foreground">
                        No alarms available. Select a machine type and device to load alarms.
                      </TableCell>
                    </TableRow>
                  ) : (
                    alarmList.map((alarm) => {
                      const alarmId = String(alarm.id);
                      const isChecked = selectedAlarmIds.has(alarmId);
                      const config = alarmConfigs[alarmId] ?? {
                        severity: 'WARNING' as const,
                        suppressionWindowValue: '',
                        suppressionWindowUnit: 'seconds' as const,
                        escalationTimeoutValue: '',
                        escalationTimeoutUnit: 'seconds' as const,
                      };
                      return (
                        <TableRow key={alarmId}>
                          <TableCell className="w-10 px-2" onClick={(e) => e.stopPropagation()}>
                            <Checkbox
                              checked={isChecked}
                              onCheckedChange={() => toggleAlarmSelection(alarmId)}
                              aria-label={`Select alarm ${alarm.alarm_text ?? alarmId}`}
                            />
                          </TableCell>
                          <TableCell className="font-medium">
                            {alarm.alarm_text ?? alarm.name ?? alarmId}
                          </TableCell>
                          <TableCell onClick={(e) => e.stopPropagation()}>
                            <Select
                              value={config.severity}
                              disabled={!isChecked}
                              onValueChange={(value) =>
                                setAlarmConfigs((prev) => ({
                                  ...prev,
                                  [alarmId]: { ...config, severity: value as 'WARNING' | 'CRITICAL' },
                                }))
                              }
                            >
                              <SelectTrigger className="h-8 border-0 shadow-none bg-transparent focus:ring-0">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="WARNING">WARNING</SelectItem>
                                <SelectItem value="CRITICAL">CRITICAL</SelectItem>
                              </SelectContent>
                            </Select>
                          </TableCell>
                          <TableCell
                            onClick={(e) => e.stopPropagation()}
                            className="p-4 align-middle min-w-[140px] max-w-[200px]"
                          >
                            <div
                              className={`flex h-8 w-full min-w-0 overflow-hidden rounded-md border border-input bg-background focus-within:ring-2 focus-within:ring-ring focus-within:ring-offset-2 ${!isChecked ? 'opacity-50' : ''}`}
                            >
                              <Input
                                type="number"
                                className="h-8 min-w-0 flex-1 border-0 shadow-none rounded-none px-2 py-0 focus-visible:ring-0 focus-visible:ring-offset-0 disabled:cursor-not-allowed"
                                value={config.suppressionWindowValue}
                                disabled={!isChecked}
                                onChange={(e) =>
                                  setAlarmConfigs((prev) => ({
                                    ...prev,
                                    [alarmId]: { ...config, suppressionWindowValue: e.target.value },
                                  }))
                                }
                                placeholder="—"
                                min={0}
                                step="any"
                                aria-label="Suppression window value"
                              />
                              <label htmlFor={`suppression-unit-${alarmId}`} className="sr-only">
                                Suppression unit (seconds, minutes, or hours)
                              </label>
                              <select
                                id={`suppression-unit-${alarmId}`}
                                className="h-8 w-[3rem] shrink-0 cursor-pointer border-0 border-l border-input bg-muted/40 px-0.5 pl-1 pr-0.5 text-[11px] font-medium tabular-nums text-foreground outline-none focus-visible:bg-muted disabled:cursor-not-allowed"
                                value={config.suppressionWindowUnit ?? 'seconds'}
                                disabled={!isChecked}
                                onChange={(e) => {
                                  const nextUnit = e.target.value as TimeUnit;
                                  setAlarmConfigs((prev) => {
                                    const cur = prev[alarmId] ?? config;
                                    const fromUnit = cur.suppressionWindowUnit ?? 'seconds';
                                    return {
                                      ...prev,
                                      [alarmId]: {
                                        ...cur,
                                        suppressionWindowUnit: nextUnit,
                                        suppressionWindowValue: convertValueForUnitChange(
                                          cur.suppressionWindowValue,
                                          fromUnit,
                                          nextUnit
                                        ),
                                      },
                                    };
                                  });
                                }}
                              >
                                <option value="seconds">sec</option>
                                <option value="minutes">min</option>
                                <option value="hours">hr</option>
                              </select>
                            </div>
                          </TableCell>
                          <TableCell
                            onClick={(e) => e.stopPropagation()}
                            className="p-4 align-middle min-w-[140px] max-w-[200px]"
                          >
                            <div
                              className={`flex h-8 w-full min-w-0 overflow-hidden rounded-md border border-input bg-background focus-within:ring-2 focus-within:ring-ring focus-within:ring-offset-2 ${!isChecked ? 'opacity-50' : ''}`}
                            >
                              <Input
                                type="number"
                                className="h-8 min-w-0 flex-1 border-0 shadow-none rounded-none px-2 py-0 focus-visible:ring-0 focus-visible:ring-offset-0 disabled:cursor-not-allowed"
                                value={config.escalationTimeoutValue}
                                disabled={!isChecked}
                                onChange={(e) =>
                                  setAlarmConfigs((prev) => ({
                                    ...prev,
                                    [alarmId]: { ...config, escalationTimeoutValue: e.target.value },
                                  }))
                                }
                                placeholder="—"
                                min={0}
                                step="any"
                                aria-label="Escalation timeout value"
                              />
                              <label htmlFor={`escalation-unit-${alarmId}`} className="sr-only">
                                Escalation unit (seconds, minutes, or hours)
                              </label>
                              <select
                                id={`escalation-unit-${alarmId}`}
                                className="h-8 w-[3rem] shrink-0 cursor-pointer border-0 border-l border-input bg-muted/40 px-0.5 pl-1 pr-0.5 text-[11px] font-medium tabular-nums text-foreground outline-none focus-visible:bg-muted disabled:cursor-not-allowed"
                                value={config.escalationTimeoutUnit ?? 'seconds'}
                                disabled={!isChecked}
                                onChange={(e) => {
                                  const nextUnit = e.target.value as TimeUnit;
                                  setAlarmConfigs((prev) => {
                                    const cur = prev[alarmId] ?? config;
                                    const fromUnit = cur.escalationTimeoutUnit ?? 'seconds';
                                    return {
                                      ...prev,
                                      [alarmId]: {
                                        ...cur,
                                        escalationTimeoutUnit: nextUnit,
                                        escalationTimeoutValue: convertValueForUnitChange(
                                          cur.escalationTimeoutValue,
                                          fromUnit,
                                          nextUnit
                                        ),
                                      },
                                    };
                                  });
                                }}
                              >
                                <option value="seconds">sec</option>
                                <option value="minutes">min</option>
                                <option value="hours">hr</option>
                              </select>
                            </div>
                          </TableCell>
                          <TableCell className="max-w-[200px] text-sm text-muted-foreground align-top">
                            {formatNotifyRoleEmails(alarm, 'operator')}
                          </TableCell>
                          <TableCell className="max-w-[200px] text-sm text-muted-foreground align-top">
                            {formatNotifyRoleEmails(alarm, 'supervisor')}
                          </TableCell>
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </div>
            <p className="text-xs text-muted-foreground mt-2">Check rows to enable editing. Only checked alarms are sent when you save.</p>
          </div>
        </div>
      </DialogContentWithoutClose>
    </Dialog>
  );
};

export default DeviceSettingsModal;
